import { memo, useMemo, useState } from "react";
import { Alert, Box, Stack, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { type Dayjs } from "dayjs";
import "dayjs/locale/pt-br";
import type { DocumentReference } from "fhir/r5";
import { CommonDialog } from "@/components/common/CommonDialog";
import { PrimaryButton } from "@/components/common/PrimaryButton";
import { SecondaryButton } from "@/components/common/SecondaryButton";
import DocumentInlinePreview from "@/pages/ecommerce/components/DocumentInlinePreview";
import { getBinaryUrl } from "../../helpers/documentDisplay";

export interface ApproveDocumentTarget {
  doc: DocumentReference;
  title: string;
  categoryLabel: string;
}

interface ApproveDocumentDialogProps {
  target: ApproveDocumentTarget;
  busy: boolean;
  onClose: () => void;
  onConfirm: (validUntil: string) => void;
}

export const ApproveDocumentDialog = memo(function ApproveDocumentDialog({
  target,
  busy,
  onClose,
  onConfirm,
}: ApproveDocumentDialogProps) {
  const [validUntil, setValidUntil] = useState<Dayjs | null>(
    dayjs().add(90, "day"),
  );
  const today = useMemo(() => dayjs().startOf("day"), []);
  const previewUrl = getBinaryUrl(target.doc);
  const canConfirm = Boolean(validUntil?.isValid() && !validUntil.isBefore(today, "day"));

  return (
    <CommonDialog
      isOpen
      handleClose={() => {
        if (!busy) onClose();
      }}
      maxWidth="md"
      sheetTitle={`Aprovar — ${target.categoryLabel}`}
      sheetSubtitle={target.title}
      footer={
        <Stack
          direction={{ xs: "column-reverse", sm: "row" }}
          spacing={1}
          justifyContent="flex-end"
        >
          <SecondaryButton
            width="fit-content"
            height="40px"
            onClick={onClose}
            disabled={busy}
          >
            Cancelar
          </SecondaryButton>
          <PrimaryButton
            width="fit-content"
            height="40px"
            disabled={!canConfirm || busy}
            onClick={() => {
              if (!validUntil) return;
              onConfirm(validUntil.format("YYYY-MM-DD"));
            }}
          >
            {busy ? "Gravando…" : "Confirmar aprovação"}
          </PrimaryButton>
        </Stack>
      }
    >
      <Box sx={{ px: { xs: 0, md: 1 }, pb: 1 }}>
        <Stack spacing={2}>
          <Alert severity="info">
            Confira o documento e informe a data de validade. A validade é
            obrigatória e fica gravada no DocumentReference.
          </Alert>

          {previewUrl ? (
            <DocumentInlinePreview
              title={target.title}
              urlBinary={previewUrl}
              onClose={onClose}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              Preview indisponível — a aprovação ainda exige a data de validade.
            </Typography>
          )}

          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
            <DatePicker
              label="Válido até"
              value={validUntil}
              minDate={today}
              onChange={(value) => setValidUntil(value)}
              format="DD/MM/YYYY"
              slotProps={{
                textField: {
                  required: true,
                  fullWidth: true,
                  helperText: "Obrigatório para confirmar a aprovação.",
                },
              }}
            />
          </LocalizationProvider>
        </Stack>
      </Box>
    </CommonDialog>
  );
});
